import React, { useEffect, useState, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import axios from "axios";
import { API_BASE_URL, getApiToken } from "../config/authConfig";

function StatusBadge({ status }) {
  const cls =
    status === "Completed"   ? "status-badge status-completed"  :
    status === "Failed"      ? "status-badge status-failed"      :
    status === "In Progress" ? "status-badge status-in-progress" :
    "status-badge status-pending";
  return <span className={cls}>{status || "Pending"}</span>;
}

function ReviewRow({ label, oldVal, newVal }) {
  const unchanged = oldVal === newVal;
  return (
    <div className={`review-row${unchanged ? " review-row-unchanged" : ""}`}>
      <span className="review-label">{label}</span>
      <span className="review-old">{oldVal || "—"}</span>
      <span className="review-arrow">→</span>
      <span className={`review-new${unchanged ? " review-unchanged-value" : ""}`}>
        {unchanged ? <span className="no-change-badge">No change</span> : (newVal || "—")}
      </span>
    </div>
  );
}

function DetailModal({ request, onClose, instance, submittedBy, onRollbackDone }) {
  const [phase, setPhase] = useState("idle"); // "idle" | "confirm" | "running" | "done" | "error"
  const [result, setResult] = useState(null);
  const [rollbackError, setRollbackError] = useState("");

  const canRollback = request.status === "Completed";

  const handleRollbackConfirm = async () => {
    setPhase("running");
    try {
      const token = await getApiToken(instance);
      const res = await axios.post(
        `${API_BASE_URL}/movement/rollback`,
        { itemId: request._itemId, submittedBy },
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
      );
      setResult(res.data);
      setPhase("done");
      onRollbackDone();
    } catch (err) {
      setRollbackError(err.response?.data?.error || "Rollback failed. Please try again.");
      setPhase("error");
    }
  };

  const formatDate = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  };

  return (
    <div className="modal-overlay" onClick={phase === "running" ? undefined : onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>

        {/* ── Header ── */}
        <div className="modal-header">
          <div>
            <span className="txn-code">{request.transactionNumber}</span>
            <StatusBadge status={request.status} />
          </div>
          <button
            className="modal-close"
            onClick={onClose}
            aria-label="Close"
            disabled={phase === "running"}
          >×</button>
        </div>

        <div className="modal-body">

          {/* ── Confirm phase ── */}
          {phase === "confirm" && (
            <div className="rollback-confirm">
              <div className="rollback-confirm-icon">↩</div>
              <p className="rollback-confirm-title">Rollback this movement?</p>
              <p className="rollback-confirm-desc">
                This will revert <strong>{request.employeeDisplayName}</strong> back to their previous
                department, job title, manager, office, and group memberships.
                A new rollback transaction will be recorded.
              </p>
            </div>
          )}

          {/* ── Running phase ── */}
          {phase === "running" && (
            <div className="rollback-running">
              <span className="search-spinner" style={{ width: 28, height: 28, borderWidth: 3 }} />
              <p>Rolling back changes for <strong>{request.employeeDisplayName}</strong>...</p>
            </div>
          )}

          {/* ── Done phase ── */}
          {phase === "done" && result && (
            <div className="rollback-result">
              <div className={`rollback-result-icon ${result.status === "Completed" ? "rollback-success" : "rollback-failed"}`}>
                {result.status === "Completed" ? "✓" : "✕"}
              </div>
              <p className="rollback-result-title">
                Rollback {result.status === "Completed" ? "completed successfully" : "completed with errors"}
              </p>
              <span className="txn-code">{result.transactionNumber}</span>
              {result.actionLog && (
                <div className="action-log" style={{ marginTop: 12 }}>
                  <div className="action-log-label">Action Log</div>
                  <pre className="action-log-content">{result.actionLog}</pre>
                </div>
              )}
            </div>
          )}

          {/* ── Error phase ── */}
          {phase === "error" && (
            <div className="rollback-result">
              <div className="rollback-result-icon rollback-failed">✕</div>
              <p className="rollback-result-title">Rollback failed</p>
              <p className="rollback-error-msg">{rollbackError}</p>
            </div>
          )}

          {/* ── Default: request details ── */}
          {(phase === "idle" || phase === "confirm") && (
            <>
              <div className="modal-details">
                <div className="detail-row">
                  <span className="detail-label">Employee</span>
                  <span className="detail-value">{request.employeeDisplayName || "—"}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">UPN</span>
                  <span className="detail-value">{request.employeeUPN || "—"}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Submitted By</span>
                  <span className="detail-value">{request.submittedBy || "—"}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Date Submitted</span>
                  <span className="detail-value">{formatDate(request.dateSubmitted)}</span>
                </div>
              </div>

              <div className="review-table" style={{ marginTop: 16 }}>
                <div className="review-header">
                  <span className="review-label">Field</span>
                  <span className="review-old">From</span>
                  <span className="review-arrow" />
                  <span className="review-new">To</span>
                </div>
                <ReviewRow label="Department" oldVal={request.oldDepartment} newVal={request.newDepartment} />
                <ReviewRow label="Job Title"  oldVal={request.oldJobTitle}   newVal={request.newJobTitle}   />
                <ReviewRow label="Manager"    oldVal={request.oldManager}    newVal={request.newManager}    />
                <ReviewRow label="Location"   oldVal={request.oldLocation}   newVal={request.newLocation}   />
                <ReviewRow label="Office"     oldVal={request.oldOffice}     newVal={request.newOffice}     />
              </div>

              {request.transactionLog && (
                <div className="action-log" style={{ marginTop: 16 }}>
                  <div className="action-log-label">Action Log</div>
                  <pre className="action-log-content">{request.transactionLog}</pre>
                </div>
              )}
            </>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="modal-footer">
          {phase === "idle" && (
            <>
              {canRollback && (
                <button className="btn-danger" onClick={() => setPhase("confirm")}>
                  Rollback
                </button>
              )}
              <button className="btn-secondary" onClick={onClose}>Close</button>
            </>
          )}
          {phase === "confirm" && (
            <>
              <button className="btn-secondary" onClick={() => setPhase("idle")}>Cancel</button>
              <button className="btn-danger" onClick={handleRollbackConfirm}>
                Yes, Rollback
              </button>
            </>
          )}
          {(phase === "done" || phase === "error") && (
            <button className="btn-secondary" onClick={onClose}>Close</button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function MovementHistoryView({ onNewRequest }) {
  const { accounts, instance } = useMsal();
  const user = accounts[0] || {};
  const submittedBy = user.username || "";

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [selectedRequest, setSelectedRequest] = useState(null);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getApiToken(instance);
      const res = await axios.get(`${API_BASE_URL}/movement/requests`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRequests(res.data.requests || []);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load movement requests.");
    } finally {
      setLoading(false);
    }
  }, [instance]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const filtered = requests.filter((r) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      r.transactionNumber?.toLowerCase().includes(q) ||
      r.employeeDisplayName?.toLowerCase().includes(q) ||
      r.employeeUPN?.toLowerCase().includes(q)
    );
  });

  const formatDate = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  };

  return (
    <div className="page-wrapper">
      <header className="app-header">
        <div className="header-inner">
          <div className="header-brand">
            <span className="header-title">Employee Movement</span>
          </div>
          <div className="header-actions">
            <button className="btn-primary btn-header" onClick={onNewRequest}>+ Submit Request</button>
            <span className="header-username">{user.name || user.username}</span>
          </div>
        </div>
      </header>

      <main className="history-container">
        <div className="history-title-row">
          <div>
            <h2 className="history-title">Movement Requests</h2>
            <p className="history-subtitle">{requests.length} total record{requests.length !== 1 ? "s" : ""}</p>
          </div>
          <div className="history-toolbar">
            <input
              type="text"
              className="history-search"
              placeholder="Search by name, UPN, or transaction number..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <button className="btn-secondary" onClick={fetchRequests}>Refresh</button>
          </div>
        </div>

        {error && <div className="history-error">{error}</div>}

        {loading && (
          <div className="history-loading">
            <span className="search-spinner" style={{ width: 24, height: 24, borderWidth: 3 }} />
            <p>Loading movement requests...</p>
          </div>
        )}

        {!loading && !error && filtered.length === 0 && (
          <div className="history-empty">
            <p>{search ? "No records match your search." : "No movement requests found."}</p>
          </div>
        )}

        {!loading && filtered.length > 0 && (
          <div className="history-card">
            <table className="history-table">
              <thead>
                <tr>
                  <th>Transaction Number</th>
                  <th>Employee</th>
                  <th>Department Change</th>
                  <th>Status</th>
                  <th>Date Submitted</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => (
                  <tr key={i} className="history-row-clickable" onClick={() => setSelectedRequest(r)}>
                    <td><span className="txn-code">{r.transactionNumber || "—"}</span></td>
                    <td className="td-name">{r.employeeDisplayName || "—"}</td>
                    <td className="td-upn">
                      {r.oldDepartment && r.newDepartment
                        ? `${r.oldDepartment} → ${r.newDepartment}`
                        : r.newDepartment || "—"}
                    </td>
                    <td><StatusBadge status={r.status} /></td>
                    <td className="td-date">{formatDate(r.dateSubmitted)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {selectedRequest && (
        <DetailModal
          request={selectedRequest}
          onClose={() => setSelectedRequest(null)}
          instance={instance}
          submittedBy={submittedBy}
          onRollbackDone={fetchRequests}
        />
      )}
    </div>
  );
}
